import api from '@services/api.service'
import { createAsyncThunk, EntityId } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { DjangoErrorResponse } from '@store/slices/generic.slice'
import { toast } from 'react-toastify'
import { PlaylistModel, playlistUrl } from '@store/slices/playlist.slice'

export const getPlaylistForTime = createAsyncThunk(
  'playlist/getByTime',
  async (props: {
    fromTime: Date
    toTime: Date
    voiceId: EntityId
    stationId: string
  }) => {
    try {
      const response = await api.get(`${playlistUrl}fetchByTime/`, {
        params: {
          fromTime: props.fromTime.toISOString(),
          toTime: props.toTime.toISOString(),
          voiceId: props.voiceId,
          stationId: props.stationId,
        },
      })
      return response.data as PlaylistModel
    } catch (error) {
      const axiosError = error as AxiosError
      if (axiosError.response) {
        const serverError = axiosError.response.data as DjangoErrorResponse
        toast.error(`${serverError.message || 'Nepodařilo se načíst playlist'}`)
      } else {
        toast.error('Nepodařilo se načíst playlist')
      }
      throw axiosError
    }
  }
)

export const checkPlaylistForTime = createAsyncThunk(
  'playlist/checkByTime',
  async (props: { fromTime: Date; toTime: Date; stationId: string }) => {
    try {
      const response = await api.get(`${playlistUrl}checkByTime/`, {
        params: {
          fromTime: props.fromTime.toISOString(),
          toTime: props.toTime.toISOString(),
          stationId: props.stationId,
        },
      })
      return response.data as EntityId[]
    } catch (error) {
      const axiosError = error as AxiosError
      if (axiosError.response) {
        const serverError = axiosError.response.data as DjangoErrorResponse
        toast.error(
          `${serverError.message || 'Nepodařilo se zkontrolovat playlist'}`
        )
      } else {
        toast.error('Nepodařilo se zkontrolovat playlist')
      }
      throw axiosError
    }
  }
)

export const updatePlaylistForRadio = createAsyncThunk(
  'playlist/updateFromRadio',
  async (props: { playlistId: EntityId }) => {
    try {
      const response = await api.get(
        `${playlistUrl}${props.playlistId}/updateFromRadio/`
      )
      return response.data as EntityId
    } catch (error) {
      const axiosError = error as AxiosError
      if (axiosError.response) {
        const serverError = axiosError.response.data as DjangoErrorResponse
        toast.error(`${serverError.message || 'Nepodařilo se načíst playlist'}`)
      } else {
        toast.error('Nepodařilo se načíst playlist')
      }
      throw axiosError
    }
  }
)

export const generatePlaylistOutline = createAsyncThunk(
  'playlist/generateOutline',
  async (props: {
    playlistId: EntityId
    linksApprovedByDefault?: boolean
    maxArticleAgeDays?: number
    randomSeed?: number
  }) => {
    const params: any = {}
    // Add optional parameters to params only if they are not undefined
    if (props.linksApprovedByDefault !== undefined)
      params.linksApprovedByDefault = props.linksApprovedByDefault
    if (props.maxArticleAgeDays !== undefined)
      params.maxArticleAgeDays = props.maxArticleAgeDays
    if (props.randomSeed !== undefined) params.randomSeed = props.randomSeed

    try {
      const response = await api.get(
        `${playlistUrl}${props.playlistId}/generate-outline/`,
        { params }
      )
      return response.data as EntityId
    } catch (error) {
      const axiosError = error as AxiosError
      if (axiosError.response) {
        const serverError = axiosError.response.data as DjangoErrorResponse
        toast.error(
          `${serverError.message || 'Nepodařilo se dramatizovat playlist'}`
        )
      } else {
        toast.error('Nepodařilo se dramatizovat playlist')
      }
      throw axiosError
    }
  }
)
export const changePlaylistDefaultVoice = createAsyncThunk(
  'playlist/changeDefaultVoice',
  async (props: { playlistId: EntityId; voiceId: EntityId }) => {
    try {
      const response = await api.post(
        `${playlistUrl}${props.playlistId}/changeDefaultVoice/`,
        { voiceId: props.voiceId }
      )
      return response.data as PlaylistModel
    } catch (error) {
      const axiosError = error as AxiosError
      if (axiosError.response) {
        const serverError = axiosError.response.data as DjangoErrorResponse
        toast.error(
          `${serverError.message || 'Nepodařilo se aktualizovat playlist'}`
        )
      } else {
        toast.error('Nepodařilo se aktualizovat playlist')
      }
      throw axiosError
    }
  }
)

export const narratePlaylist = createAsyncThunk(
  'playlist/narratePlaylist',
  async (props: { playlistId: EntityId; generateSpeech?: boolean }) => {
    const params: any = {}
    if (props.generateSpeech !== undefined)
      params.generateSpeech = props.generateSpeech
    try {
      const response = await api.get(
        `${playlistUrl}${props.playlistId}/narrate/`,
        { params }
      )
      return response.data as EntityId
    } catch (error) {
      const axiosError = error as AxiosError
      if (axiosError.response) {
        const serverError = axiosError.response.data as DjangoErrorResponse
        toast.error(
          `${serverError.message || 'Nepodařilo se vytvorit narace pro playlist'}`
        )
      } else {
        toast.error('Nepodařilo se vytvorit narace playlist')
      }
      throw axiosError
    }
  }
)

export const transcribePlaylist = createAsyncThunk(
  'playlist/transcribePlaylist',
  async (props: { playlistId: EntityId }) => {
    try {
      const response = await api.get(
        `${playlistUrl}${props.playlistId}/transcribe/`
      )
      return response.data as EntityId
    } catch (error) {
      const axiosError = error as AxiosError
      if (axiosError.response) {
        const serverError = axiosError.response.data as DjangoErrorResponse
        toast.error(
          `${serverError.message || 'Nepodařilo se vytvořit transcripci dialogů pro playlist'}`
        )
      } else {
        toast.error('Nepodařilo se vytvorit transcripci dialogů')
      }
      throw axiosError
    }
  }
)

export const publishUpdatedForPlaylist = createAsyncThunk(
  'playlist/publishUpdated',
  async (playlistId: EntityId) => {
    try {
      const response = await api.get(
        `${playlistUrl}${playlistId}/publishUpdated/`
      )
      return response.data as EntityId
    } catch (error) {
      const axiosError = error as AxiosError
      if (axiosError.response) {
        const serverError = axiosError.response.data as DjangoErrorResponse
        toast.error(
          `${serverError.message || 'Nepodařilo se publikovat audia pro playlist'}`
        )
      } else {
        toast.error('Nepodařilo se publikovat audia pro playlist')
      }
      throw axiosError
    }
  }
)
